import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimWorkOrderVersionOtherPriceData } from 'gql/graphql';
import { SortEnumType } from 'gql/graphql';
import { getDefaultFilterCleaner, usePageFilterSaver, withDefaultTransformer } from 'hooks/usePageFilterSaver';
import { isNotEmpty } from 'utils';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
    OTHER_TABLE_GROUP_NAME,
    getActsKc2OtherCostAcceptedKey,
    getActsKc2OtherCostCommentKey,
    getActsKc2OtherCostPlanKey,
} from '../../WorkOrder/types';
import { bimWorkOrderVersionOtherPricesGql } from '../workOrderVersionQueries';

interface IUseWorkOrderOtherPriceList {
    workOrderId: number;
    workOrderVersionId: number;
    workOrderRootObjectId: number | undefined;
    withEffect?: boolean;
}

const getGroupedOthers = (itemsArr: BimWorkOrderVersionOtherPriceData[] | undefined | null) => {
    if (!itemsArr) {
        return [];
    }

    const updateItemsArr = itemsArr.map((item) => ({
        ...item,
        planValue: {
            ...item.planValue,
            total: item.planValue?.total === null ? item.planValue.others : item.planValue?.total,
        },
    }));

    const itemsByGroup = updateItemsArr?.reduce((acc: any, item) => {
        const groupName = item.section;

        const flatAct =
            item.actKC2OtherCosts?.reduce((accum: Record<string, number | string>, cost) => {
                accum[getActsKc2OtherCostPlanKey(cost.actKC2Id)] = cost.costPlan;
                accum[getActsKc2OtherCostAcceptedKey(cost.actKC2Id)] = cost.costAccepted;
                accum[getActsKc2OtherCostCommentKey(cost.actKC2Id)] = cost.comment || '';

                return accum;
            }, {}) || {};

        const mappedItem = {
            ...item,
            ...flatAct,
            planValueOthers: item?.planValue?.others,
            planValueTotal: item?.planValue?.total,
            completedValueOthers: item?.completedValue?.others,
            completedValueTotal: item?.completedValue?.total,
            remainderValueOthers: item?.remainderValue?.others,
            remainderValueTotal: item?.remainderValue?.total,
        };

        if (groupName) {
            if (acc[groupName]) {
                acc[groupName].push(mappedItem);
            } else {
                acc[groupName] = [
                    { id: groupName, name: OTHER_TABLE_GROUP_NAME[groupName], isGroupTitle: true },
                    mappedItem,
                ];
            }
        }

        return acc;
    }, {});

    return Object.values(itemsByGroup).reduce((acc: any[], group) => acc.concat(group), []);
};

export const useWorkOrderOtherVersionPriceList = (props: IUseWorkOrderOtherPriceList) => {
    const dispatch = useDispatch();
    const { workOrderVersionId, workOrderRootObjectId, withEffect = true } = props;

    const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});

    const variables = useMemo(
        () => ({
            workOrderVersionId,
            workOrderVersionRootObjectId: workOrderRootObjectId,
            take: 1000,
            skip: 0,
            order: [{ name: SortEnumType.Asc }],
        }),
        [workOrderRootObjectId, workOrderVersionId],
    );

    const changeFilterHandler = useCallback(({ fieldName, checkedValues }) => {
        setFilterValues((prevValue) => ({
            ...prevValue,
            [fieldName]: checkedValues,
        }));
    }, []);

    const { canSendRequest } = usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: withDefaultTransformer(
                    changeFilterHandler,
                    `others_${workOrderRootObjectId}`,
                    getDefaultFilterCleaner({
                        changeFilterHandler,
                        setFiltersWhere: setFilterValues,
                    }),
                ),
            }),
            [changeFilterHandler, workOrderRootObjectId],
        ),
    );

    const [othersPriceData, othersPriceDataStatus, , otherPriceRequest] = useRequest({
        requestId: 'bimWorkOrderVersionOtherPrices',
        query: bimWorkOrderVersionOtherPricesGql,
        variables,
        withEffect: withEffect && canSendRequest,
    });

    const [resetData] = useResetStoreData({ requestId: 'bimWorkOrderVersionOtherPrices' });

    useEffect(() => {
        if (withEffect) {
            resetData(null);
        }
    }, [resetData, withEffect]);

    const refreshWorkOrderOtherPriceList = useCallback(() => {
        dispatch(otherPriceRequest(variables));
    }, [dispatch, otherPriceRequest, variables]);

    const totalItems = useMemo(
        () => othersPriceData?.bimWorkOrderVersionOtherPrices?.totalCount,
        [othersPriceData?.bimWorkOrderVersionOtherPrices?.totalCount],
    );

    const fetchedItemsCounter = useMemo(
        () => othersPriceData?.bimWorkOrderVersionOtherPrices?.items?.length,
        [othersPriceData?.bimWorkOrderVersionOtherPrices?.items?.length],
    );

    const nameFilterItems = useMemo(
        () => othersPriceData?.bimWorkOrderVersionOtherPrices?.items?.map((item) => ({ label: item.name })) ?? [],
        [othersPriceData?.bimWorkOrderVersionOtherPrices?.items],
    );

    const tableItems = useMemo(() => {
        const hasActiveFilter = Object.values(filterValues ?? {}).some(isNotEmpty);

        const filteredItems = hasActiveFilter
            ? othersPriceData?.bimWorkOrderVersionOtherPrices?.items?.filter((item) =>
                  filterValues.name.includes(item.name),
              )
            : othersPriceData?.bimWorkOrderVersionOtherPrices?.items;

        return getGroupedOthers(filteredItems);
    }, [filterValues, othersPriceData?.bimWorkOrderVersionOtherPrices?.items]);

    return {
        tableItems,
        totalItems,
        fetchedItemsCounter,
        refreshWorkOrderOtherPriceList,
        changeFilterHandler,
        nameFilterItems,
        othersPriceDataStatus,
    };
};
